import { Component, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, Skeleton, ThemeProvider, createTheme, Typography } from "@mui/material";
import { Document, Page, PasswordResponses, pdfjs } from "react-pdf";
import {
	type CoreFileItem,
	CoreBanner,
	CoreIconButton,
	CoreModal,
	CoreNewTabIcon,
	ModalContext,
	OverflowText,
	useOpenInNewTab,
} from "../core";
import i18nPackage from "../i18n/i18n";
import ZoomInIcon from "./images/zoom-in.svg?react";
import ZoomOutIcon from "./images/zoom-out.svg?react";
import LeftArrowIcon from "./images/left-arrow.svg?react";
import RightArrowIcon from "./images/right-arrow.svg?react";
import styles from "./PdfViewer.module.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
// `new URL("pdfjs-dist/...", import.meta.url)` resolves a bare node_modules specifier, which Vite
// only bundles correctly in production builds - under `vite dev` it produces a broken URL and
// pdfjs falls back to (and then fails at) its "fake worker" mode. The explicit `?url` suffix is
// handled consistently by Vite in both dev and build.
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

// Must be set in the same module that renders <Document>/<Page> - see react-pdf's worker setup docs.
const PDF_WORKER_URL = pdfWorkerUrl;
pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL;

// Prod only: our CDN serves .mjs as application/octet-stream, which browsers refuse to load as a
// module, so re-fetch and re-wrap as a Blob with an explicit JS MIME type. Skipped in dev, where
// Vite already serves the worker with a correct content-type and this rewrap would instead break
// Vite's injected HMR client import (blob: URLs have no base to resolve it against).
//
// Lazy (not run at module scope) so importing this module alone doesn't trigger the fetch.
let workerSrcReadyPromise: Promise<void> | undefined;

function getWorkerSrcReady(): Promise<void> {
	if (import.meta.env.DEV) {
		return Promise.resolve();
	}
	if (!workerSrcReadyPromise) {
		workerSrcReadyPromise = fetch(PDF_WORKER_URL)
			.then((response) => {
				if (!response.ok) {
					throw new Error(`Failed to fetch PDF worker script (status ${response.status})`);
				}
				return response.arrayBuffer();
			})
			.then((buffer) => {
				const blob = new Blob([buffer], { type: "text/javascript" });
				pdfjs.GlobalWorkerOptions.workerSrc = URL.createObjectURL(blob);
			})
			.catch((error: unknown) => {
				console.error("PdfViewer: failed to fetch PDF worker script, falling back to direct URL", error);
			});
	}
	return workerSrcReadyPromise;
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.25;
const PAGE_HORIZONTAL_PADDING = 48;
// Approximates a US Letter page's aspect ratio (11in / 8.5in).
const PAGE_ASPECT_RATIO = 1.294;

// pdfjs.getDocument() can throw synchronously (e.g. "Invalid PDF binary data") when handed a
// malformed source. react-pdf's <Document> calls it from inside a useEffect without a
// try/catch, so an uncaught throw there crashes past this component - with no boundary,
// React unmounts the entire tree that rendered it, and any host (like the extension's
// reference material panel, which only creates its React root once) never recovers.
class PdfDocumentErrorBoundary extends Component<
	{
		children: ReactNode;
		loadErrorLabel: string;
		retryLabel: string;
		bannerClassName: string;
		retryLinkClassName: string;
		onError: (message: string) => void;
		onRetry: () => void;
	},
	{ hasError: boolean }
> {
	state = { hasError: false };

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error: unknown) {
		this.props.onError(error instanceof Error ? error.message : String(error));
	}

	render() {
		if (this.state.hasError) {
			return (
				<CoreBanner label={this.props.loadErrorLabel} color="primary" className={this.props.bannerClassName}>
					<Link
						component="button"
						type="button"
						underline="hover"
						className={this.props.retryLinkClassName}
						onClick={this.props.onRetry}
					>
						{this.props.retryLabel}
					</Link>
				</CoreBanner>
			);
		}
		return this.props.children;

	}
}

function PdfPageSkeleton({ width }: { width: number }) {
	return (
		<div className={styles.pdfSkeletonPage} style={{ width, height: width * PAGE_ASPECT_RATIO }}>
			<Skeleton variant="text" width="55%" height={32} />
			<Skeleton variant="text" width="90%" />
			<Skeleton variant="text" width="95%" />
			<Skeleton variant="text" width="85%" />
			<Skeleton variant="text" width="40%" />
			<Skeleton variant="rounded" width="100%" height={120} sx={{ marginTop: "16px" }} />
			<Skeleton variant="text" width="90%" sx={{ marginTop: "16px" }} />
			<Skeleton variant="text" width="70%" />
		</div>
	);
}

export function PdfViewer({
	file,
	showOpenInNewTab = true,
	tooltipContainer,
}: {
	file: CoreFileItem;
	showOpenInNewTab?: boolean;
	/** Portal target for tooltips; required in a shadow DOM, else styles/stacking break. */
	tooltipContainer?: HTMLElement;
}) {
	const [numPages, setNumPages] = useState(0);
	const [pageNumber, setPageNumber] = useState(1);
	const [pageInputValue, setPageInputValue] = useState("1");
	const [scale, setScale] = useState(1);
	const [fitWidth, setFitWidth] = useState<number | undefined>(undefined);
	const [loadError, setLoadError] = useState<string | null>(null);
	// Derived in onLoadError. Rejecting onPassword's callback doesn't transmit our error's content
	// to the pdf.js worker - it just makes the worker treat the password as empty, which throws
	// its own fixed PasswordException("No password given"). That's the actual, observed signal.
	const [isPasswordCanceled, setIsPasswordCanceled] = useState(false);
	const [resolvedFile, setResolvedFile] = useState<File | Blob | { url: string } | undefined>(file.nativeFile);
	const [workerReady, setWorkerReady] = useState(false);
	// Bumped to force <Document> to remount (fresh getDocument() call) after a password is
	// cancelled/rejected, so the user gets another prompt instead of being stuck.
	const [reloadKey, setReloadKey] = useState(0);
	// Set by onPassword, rendering our own in-panel modal instead of the tab-blocking native
	// prompt().
	const [passwordRequest, setPasswordRequest] = useState<{
		callback: (value: string | Error | null) => void;
		reason: number;
	} | null>(null);
	const [passwordInputValue, setPasswordInputValue] = useState("");
	// Callback ref (not a plain ref) because the modal's container needs to react once this
	// mounts - a plain ref's mutation wouldn't trigger the re-render modalTheme depends on.
	const [viewerWrapperEl, setViewerWrapperEl] = useState<HTMLDivElement | null>(null);
	const { handleOpenInNewTab } = useOpenInNewTab(file);
	const name = file.name;

	const retryLoad = () => {
		setLoadError(null);
		setIsPasswordCanceled(false);
		setReloadKey((key) => key + 1);
	};

	// The specific message/name here don't survive to onLoadError (see isPasswordCanceled above) -
	// what matters is that it's an Error instance, which makes onPassword's callback reject the
	// load instead of resolving with a bad password and looping into another prompt.
	const cancelPasswordError = () => new Error(i18nPackage.t("coreComponents.pdfViewer.passwordCanceledError"));

	const submitPassword = () => {
		if (!passwordRequest) {
			return;
		}
		passwordRequest.callback(passwordInputValue);
		setPasswordRequest(null);
	};

	const cancelPassword = () => {
		if (!passwordRequest) {
			return;
		}
		passwordRequest.callback(cancelPasswordError());
		setPasswordRequest(null);
	};

	useEffect(() => {
		let cancelled = false;
		void getWorkerSrcReady().then(() => {
			if (!cancelled) {
				setWorkerReady(true);
			}
		});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		// Reset per-file state whenever the source changes.
		setNumPages(0);
		setPageNumber(1);
		setPageInputValue("1");
		setLoadError(null);
		setIsPasswordCanceled(false);
		setPasswordRequest(null);

		if (file.nativeFile) {
			setResolvedFile(file.nativeFile);
			return;
		}

		setResolvedFile(file.url ? { url: file.url } : undefined);
	}, [file.nativeFile, file.url]);
	const tooltipSlotProps = {
		tooltip: {
			slotProps: {
				popper: { container: tooltipContainer },
			},
		},
	};

	// CoreModal only self-targets its container via ModalContext in mobile layout; on desktop it
	// defers to the ambient MUI theme's Modal/Popover/Popper container defaults. Same pattern as
	// editQuizModalWrapper.tsx's iframeTheme. Always scoping to our own wrapper div (rather than
	// tooltipContainer/document.body) keeps the password modal contained within this component,
	// in both the shadow-DOM extension and the regular web app.
	const modalTheme = useMemo(() => {
		if (!viewerWrapperEl) {
			return undefined;
		}
		return createTheme({
			components: {
				MuiModal: { defaultProps: { container: viewerWrapperEl } },
				MuiPopover: { defaultProps: { container: viewerWrapperEl } },
				MuiPopper: { defaultProps: { container: viewerWrapperEl } },
			},
		});
	}, [viewerWrapperEl]);

	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
	const isEditingPageInputRef = useRef(false);
	const pageInputRef = useRef<HTMLInputElement | null>(null);

	// delegatesFocus (prepareShadowDom) blocks the usual click-elsewhere blur anywhere in the shadow
	// tree, so listen on tooltipContainer (the shadow root) to blur it ourselves. Regular web app
	// usage (no tooltipContainer) is unaffected - native blur-on-click-elsewhere already works.
	useEffect(() => {
		if (!tooltipContainer) {
			return;
		}

		const handleMouseDown = (event: MouseEvent) => {
			if (event.target !== pageInputRef.current) {
				pageInputRef.current?.blur();
			}
		};

		tooltipContainer.addEventListener("mousedown", handleMouseDown);
		return () => tooltipContainer.removeEventListener("mousedown", handleMouseDown);
	}, [tooltipContainer]);

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) {
			return;
		}

		const updateFitWidth = () => {
			setFitWidth(Math.max(0, container.clientWidth - PAGE_HORIZONTAL_PADDING));
		};

		updateFitWidth();

		const resizeObserver = new ResizeObserver(updateFitWidth);
		resizeObserver.observe(container);

		return () => resizeObserver.disconnect();
	}, []);

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container || numPages === 0) {
			return;
		}

		// IntersectionObserver's callback only reports entries whose ratio just crossed a
		// threshold, not every observed page's current state - so picking "most visible" from
		// just that batch can pick a stale/wrong page. Keep a running ratio per page instead.
		const ratioByPage = new Map<Element, number>();

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					ratioByPage.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
				}

				let mostVisibleIndex = -1;
				let mostVisibleRatio = 0;
				pageRefs.current.forEach((page, index) => {
					const ratio = (page && ratioByPage.get(page)) || 0;
					if (ratio > mostVisibleRatio) {
						mostVisibleRatio = ratio;
						mostVisibleIndex = index;
					}
				});

				if (mostVisibleIndex !== -1) {
					setPageNumber(mostVisibleIndex + 1);
				}
			},
			{ root: container, threshold: [0, 0.25, 0.5, 0.75, 1] },
		);

		pageRefs.current.forEach((page) => page && observer.observe(page));

		return () => observer.disconnect();
	}, [numPages]);

	useEffect(() => {
		if (!isEditingPageInputRef.current) {
			setPageInputValue(String(pageNumber));
		}
	}, [pageNumber]);

	const scrollToPage = (page: number) => {
		const container = scrollContainerRef.current;
		const target = pageRefs.current[page - 1];
		if (!container || !target) {
			return;
		}
		const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
		container.scrollTo({ top: offset, behavior: "auto" });
	};

	const commitPageInput = (rawValue: string) => {
		const parsed = Number.parseInt(rawValue, 10);
		const clamped = Number.isNaN(parsed) ? pageNumber : Math.min(Math.max(parsed, 1), Math.max(numPages, 1));
		setPageInputValue(String(clamped));
		scrollToPage(clamped);
	};

	const isDocumentLoading = numPages === 0 && !loadError;

	const pageNumberLabel = i18nPackage.t("coreComponents.pdfViewer.pageNumberAriaLabel");
	const zoomOutLabel = i18nPackage.t("coreComponents.pdfViewer.zoomOutAriaLabel");
	const zoomInLabel = i18nPackage.t("coreComponents.pdfViewer.zoomInAriaLabel");
	const previousPageLabel = i18nPackage.t("coreComponents.pdfViewer.previousPageLabel");
	const nextPageLabel = i18nPackage.t("coreComponents.pdfViewer.nextPageLabel");
	const openInNewTabLabel = i18nPackage.t("coreComponents.pdfViewer.openInNewTabAriaLabel");
	const loadErrorLabel = i18nPackage.t("coreComponents.pdfViewer.loadErrorMessage");

	// <Document> must stay mounted while loading - it's what fetches/parses and calls onLoadSuccess.
	const pageScrollContent = (
		<div className={styles.pdfPageScroll} ref={scrollContainerRef} role="document" aria-label={name}>
			<div className={styles.spacer}></div>
			<PdfDocumentErrorBoundary
				key={reloadKey}
				loadErrorLabel={loadErrorLabel}
				retryLabel={i18nPackage.t("coreComponents.pdfViewer.retryLabel")}
				bannerClassName={styles.pdfLoadBanner}
				retryLinkClassName={styles.pdfRetryLink}
				onError={(message) => {
					setLoadError(message);
					setIsPasswordCanceled(false);
				}}
				onRetry={retryLoad}
			>
				<Document
					file={workerReady ? resolvedFile : undefined}
					className={styles.pdfDocument}
					loading={<PdfPageSkeleton width={fitWidth ?? 320} />}
					noData={
						loadError ? (
							<CoreBanner label={loadErrorLabel} color="primary" className={styles.pdfLoadBanner} />
						) : (
							<PdfPageSkeleton width={fitWidth ?? 320} />
						)
					}
					error={
						<CoreBanner label={loadErrorLabel} color="primary" className={styles.pdfLoadBanner}>
							{isPasswordCanceled && (
								<Link
									component="button"
									type="button"
									underline="hover"
									className={styles.pdfRetryLink}
									onClick={retryLoad}
								>
									{i18nPackage.t("coreComponents.pdfViewer.retryLabel")}
								</Link>
							)}
						</CoreBanner>
					}
					externalLinkTarget="_blank"
					externalLinkRel="noopener noreferrer"
					onLoadSuccess={({ numPages: loadedNumPages }: { numPages: number }) => {
						pageRefs.current = new Array(loadedNumPages).fill(null);
						setNumPages(loadedNumPages);
						setPageNumber(1);
						setLoadError(null);
					}}
					onLoadError={(error) => {
						console.error("PdfViewer: failed to load PDF", error);
						setLoadError(error.message);
						setIsPasswordCanceled(error.name === "PasswordException" && error.message === "No password given");
					}}
					onPassword={(callback, reason) => {
						// Renders our own in-panel modal (below) instead of the tab-blocking native
						// prompt(). Cancel calls cancelPasswordError() via cancelPassword(), which
						// rejects (rather than looping back into another prompt) - see its comment
						// for why the content doesn't matter, and isPasswordCanceled for the actual
						// observed error signal.
						setPasswordInputValue("");
						setPasswordRequest({ callback: callback as (value: string | Error | null) => void, reason });
					}}
				>
					{fitWidth &&
						Array.from({ length: numPages }, (_, index) => (
							<div key={index} ref={(page) => (pageRefs.current[index] = page)} className={styles.pdfPageItem}>
								<Page pageNumber={index + 1} width={fitWidth} scale={scale} loading={null} noData={null} />
							</div>
						))}
				</Document>
			</PdfDocumentErrorBoundary>
			<div className={styles.spacer}></div>
		</div>
	);

	const passwordModal = passwordRequest && (
		<CoreModal
			open
			title={i18nPackage.t("coreComponents.pdfViewer.passwordModalTitle")}
			onClose={cancelPassword}
			buttons={[
				{ text: i18nPackage.t("coreComponents.pdfViewer.cancelLabel"), color: "cancel", onClick: cancelPassword },
				{ text: i18nPackage.t("coreComponents.pdfViewer.submitLabel"), onClick: submitPassword },
			]}
		>
			<Typography className={styles.pdfPasswordInputDescription}>
				{passwordRequest.reason === PasswordResponses.INCORRECT_PASSWORD
					? i18nPackage.t("coreComponents.pdfViewer.incorrectPasswordPrompt")
					: i18nPackage.t("coreComponents.pdfViewer.enterPasswordPrompt")}
			</Typography>
			<input
				type="password"
				autoFocus
				className={styles.pdfPasswordInput}
				value={passwordInputValue}
				onChange={(event) => setPasswordInputValue(event.target.value)}
				onKeyDown={(event) => {
					if (event.key === "Enter") {
						submitPassword();
					}
				}}
				aria-label={i18nPackage.t("coreComponents.pdfViewer.passwordInputAriaLabel")}
			/>
		</CoreModal>
	);

	// CoreModal's mobile-layout container comes from ModalContext; desktop comes from modalTheme's
	// MuiModal defaultProps (see modalTheme above). Both point at viewerWrapperEl, so either
	// layout path keeps the modal contained within this component instead of document.body.
	const passwordModalContent = viewerWrapperEl && (
		<ThemeProvider theme={modalTheme!}>
			<ModalContext.Provider value={{ modalContainer: { current: viewerWrapperEl } }}>
				{passwordModal}
			</ModalContext.Provider>
		</ThemeProvider>
	);

	if (isDocumentLoading) {
		return (
			<div className={styles.pdfViewerWrapper} ref={setViewerWrapperEl}>
				<div className={styles.pdfToolbar}>
					<Skeleton variant="rounded" width="60%" height={20} className={styles.pdfTitle} />
					<div className={styles.pdfToolbarControls}>
						<Skeleton variant="rounded" width={72} height={24} />
						<Skeleton variant="rounded" width={96} height={24} />
					</div>
					{showOpenInNewTab && (
						<div className={styles.pdfToolbarActions}>
							<Skeleton variant="rounded" width={28} height={28} />
						</div>
					)}
				</div>
				{pageScrollContent}
				{passwordModalContent}
			</div>
		);
	}

	return (
		<div className={styles.pdfViewerWrapper} ref={setViewerWrapperEl}>
			<div className={styles.pdfToolbar}>
				<OverflowText className={styles.pdfTitle} tooltipProps={tooltipSlotProps.tooltip}>
					{name}
				</OverflowText>
				{!loadError && (
					<>
						<div className={styles.pdfToolbarControls}>
							<div className={styles.pdfToolbarGroup}>
								<CoreIconButton
									variant="plain"
									onClick={() => {
										const nextPageNumber = Math.max(1, pageNumber - 1);
										setPageNumber(nextPageNumber);
										scrollToPage(nextPageNumber);
									}}
									disabled={pageNumber <= 1}
									tooltip={previousPageLabel}
									aria-label={previousPageLabel}
									slotProps={tooltipSlotProps}
								>
									<LeftArrowIcon className={styles.pdfPageArrowIcon} />
								</CoreIconButton>
								<input
									ref={pageInputRef}
									type="text"
									inputMode="numeric"
									className={styles.pdfPageInput}
									value={pageInputValue}
									aria-label={pageNumberLabel}
									title={pageNumberLabel}
									onFocus={() => {
										isEditingPageInputRef.current = true;
									}}
									onChange={(event) => setPageInputValue(event.target.value.replace(/\D/g, ""))}
									onBlur={(event) => {
										isEditingPageInputRef.current = false;
										commitPageInput(event.target.value);
									}}
									onKeyDown={(event) => {
										if (event.key === "Enter") {
											event.currentTarget.blur();
										}
									}}
								/>
								<span className={styles.pdfPageLabel}>/&nbsp;&nbsp;{numPages}</span>
								<CoreIconButton
									variant="plain"
									onClick={() => {
										const nextPageNumber = Math.min(numPages, pageNumber + 1);
										setPageNumber(nextPageNumber);
										scrollToPage(nextPageNumber);
									}}
									disabled={pageNumber >= numPages}
									tooltip={nextPageLabel}
									aria-label={nextPageLabel}
									slotProps={tooltipSlotProps}
								>
									<RightArrowIcon className={styles.pdfPageArrowIcon} />
								</CoreIconButton>
							</div>
							{showOpenInNewTab && (
								<div className={styles.pdfToolbarGroup}>
									<CoreIconButton
										variant="plain"
										onClick={() => setScale((current) => Math.max(MIN_SCALE, current - SCALE_STEP))}
										disabled={scale <= MIN_SCALE}
										tooltip={zoomOutLabel}
										aria-label={zoomOutLabel}
										slotProps={tooltipSlotProps}
									>
										<ZoomOutIcon />
									</CoreIconButton>
									<span className={styles.pdfPageLabel}>{Math.round(scale * 100)}%</span>
									<CoreIconButton
										variant="plain"
										onClick={() => setScale((current) => Math.min(MAX_SCALE, current + SCALE_STEP))}
										disabled={scale >= MAX_SCALE}
										tooltip={zoomInLabel}
										aria-label={zoomInLabel}
										slotProps={tooltipSlotProps}
									>
										<ZoomInIcon />
									</CoreIconButton>
								</div>
							)}
						</div>
						{showOpenInNewTab ? (
							<div className={styles.pdfToolbarActions}>
								<CoreIconButton
									variant="plain"
									onClick={() => handleOpenInNewTab()}
									tooltip={openInNewTabLabel}
									aria-label={openInNewTabLabel}
									slotProps={tooltipSlotProps}
								>
									<CoreNewTabIcon />
								</CoreIconButton>
							</div>
						) : (
							<div className={styles.pdfToolbarGroup} style={{ justifyContent: "end" }}>
								<CoreIconButton
									variant="plain"
									onClick={() => setScale((current) => Math.max(MIN_SCALE, current - SCALE_STEP))}
									disabled={scale <= MIN_SCALE}
									tooltip={zoomOutLabel}
									aria-label={zoomOutLabel}
									slotProps={tooltipSlotProps}
								>
									<ZoomOutIcon />
								</CoreIconButton>
								<span className={styles.pdfPageLabel}>{Math.round(scale * 100)}%</span>
								<CoreIconButton
									variant="plain"
									onClick={() => setScale((current) => Math.min(MAX_SCALE, current + SCALE_STEP))}
									disabled={scale >= MAX_SCALE}
									tooltip={zoomInLabel}
									aria-label={zoomInLabel}
									slotProps={tooltipSlotProps}
								>
									<ZoomInIcon />
								</CoreIconButton>
							</div>
						)}
					</>
				)}
			</div>
			{pageScrollContent}
		</div>
	);
}

// Default export so consumers can React.lazy() this module (needs a { default } shape).
export default PdfViewer;
